// Copyright (c) Docugami, Inc. All rights reserved.

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { ReportEntry } from 'networking/reportsApi';
import { useEffect, useMemo, useRef, useState } from 'react';
import { selectDocsetLabels } from 'store/dgml/dgmlSelectors';
import { createOperationId, setActiveAddEntryGroupRequest, useAddGroupMutation, useLabelEntryMutation, useLabelGroupMutation } from 'store/reports/reportsSlice';
import { createSelectEntryGroupLabels } from 'store/reports/selectors';
import styled from 'styled-components';
import { MAX_TAG_LENGTH, getValidatedDecodedTagErrorMessage, isValidDecodedTag } from 'utils/TagStandardUtils';
import { HintInputField, IInputField } from './HintInputField';

interface ReportEntryItemTitleInputFieldProps {
    entry: ReportEntry;
    reportId: string;
    readonly?: boolean;
}

export const ReportEntryItemTitleInputField = (props: ReportEntryItemTitleInputFieldProps) => {
    const [editValue, setEditValue] = useState<string | undefined>();

    const docsetLabels = useAppSelector(state => selectDocsetLabels(state, state.workPaths.currentWorkPath!.clusterId));
    const existingLabels = useMemo(() => docsetLabels.filter(x => x !== props.entry.title), [props.entry.title, docsetLabels]);

    const [trigger] = useLabelEntryMutation();

    async function onConfirm(value: string) {
        await trigger({
            reportId: props.entry.reportId,
            entryId: props.entry.id,
            label: value,
            operationId: createOperationId()
        });
    }

    async function onAccept() {
        if (!editValue || !isValidDecodedTag(editValue.trim())) {
            throw new Error(`Invalid tag text: ${editValue}`);
        }

        await onConfirm(editValue.trim());
        setEditValue(undefined);
    }

    function onCancel() {
        setEditValue(undefined);
    }

    function isValidLabel(label: string) {
        return !getValidatedLabelErrorMessage(label, existingLabels);
    }

    const isEditing = editValue !== undefined;
    const title = editValue ?? props.entry.title;

    return (
        <HintInputField
            value={title}
            placeholder={props.entry.title}
            onChange={(value: string) => setEditValue(value)}
            isEditing={!props.readonly ? isEditing : false}
            onEdit={!props.readonly ? () => setEditValue(title) : undefined}
            onCancel={onCancel}
            isValid={(value: string) => isValidLabel(value.trim())}
            maxLength={MAX_TAG_LENGTH}
            onAccept={onAccept}
            onBlur={onCancel}
            getErrorText={label => getValidatedLabelErrorMessage(label, existingLabels)} />
    )
}

const GroupHintInputField = styled(HintInputField)`
    text-align: center;
    background: white;
    border-radius: 10px;

    input {
        text-align: center;
    }   
`;

interface ReportGroupItemTitleInputFieldProps {
    reportId: string;
    groupId: string;
    groupLabel: string;
    readonly?: boolean;
}

const selectGroupLabels = createSelectEntryGroupLabels();
export const ReportGroupItemTitleInputField = (props: ReportGroupItemTitleInputFieldProps) => {
    const [editValue, setEditValue] = useState<string | undefined>();

    const addGroupRequest = useAppSelector(state => state.reportOperations.requests.activeAddEntryGroupRequest);
    const isAddGroupRequest = props.groupId === addGroupRequest?.id;

    const groupLabels = useAppSelector(state => selectGroupLabels(state, props.reportId));
    const docsetLabels = useAppSelector(state => selectDocsetLabels(state, state.workPaths.currentWorkPath!.clusterId));
    const existingLabels = useMemo(() => [...docsetLabels, ...groupLabels.filter(x => !stringEqualsIgnoreCase(x, props.groupLabel.trim()))], [groupLabels, docsetLabels, props.groupLabel]);

    const dispatch = useAppDispatch();

    const [triggerLabel, { isLoading: isLabelling }] = useLabelGroupMutation();
    const [triggerSave, { isLoading: isSaving }] = useAddGroupMutation();

    const inputRef = useRef<IInputField>();

    async function onConfirm(value: string) {
        if (isLabelling || isSaving) {
            return;
        }

        if (isAddGroupRequest) {
            await handleAddGroupRequest();
        } else {
            await triggerLabel({
                reportId: props.reportId,
                groupId: props.groupId,
                label: value,
                operationId: createOperationId()
            });
        }
    }

    async function onAccept() {
        if (!editValue || !isValidLabel(editValue.trim())) {
            throw new Error(`Invalid tag text: ${editValue}`);
        }

        await onConfirm(editValue.trim());
        setEditValue(undefined);
    }

    function onCancel() {
        setEditValue(undefined);
    }

    async function handleAddGroupRequest() {
        if (!addGroupRequest) {
            return;
        }

        const label = editValue ?? addGroupRequest?.label;
        if (!!label) {
            await triggerSave({
                reportId: props.reportId,
                label: label,
                entries: addGroupRequest?.entries,
                operationId: createOperationId()
            });
        }

        dispatch(setActiveAddEntryGroupRequest(undefined));
    }

    async function onBlur() {
        // When focusing out, handle any add group request
        if (!!isAddGroupRequest) {
            await handleAddGroupRequest();
        }
        onCancel();
    }

    const isValidLabel = (label: string) => !!label.trim() && !getValidatedGroupLabelErrorMessage(label);

    function getValidatedGroupLabelErrorMessage(label: string) {
        if (isLabelling || isSaving) {
            // Operation is in progress
            return '';
        }

        if (!label && isAddGroupRequest) {
            // This is a non-error scenario. The new group request starts with a blank label
            return '';
        }

        return getValidatedLabelErrorMessage(label.trim(), existingLabels);
    }

    useEffect(() => {
        if (!!isAddGroupRequest) {
            inputRef.current?.focus();
            setEditValue("");
        }
    }, [isAddGroupRequest])

    const isEditing = editValue !== undefined;
    const title = editValue ?? props.groupLabel;

    return (
        <GroupHintInputField
            inputRef={inputRef}
            value={title}
            disabled={isLabelling || isSaving}
            placeholder={isAddGroupRequest ? "Enter a group label" : props.groupLabel}
            onChange={(value: string) => setEditValue(value)}
            isEditing={!props.readonly ? isEditing : false}
            onEdit={!props.readonly ? () => setEditValue(title) : undefined}
            onCancel={onCancel}
            isValid={(value: string) => isValidLabel(value.trim())}
            maxLength={MAX_TAG_LENGTH}
            onAccept={onAccept}
            onBlur={onBlur}
            getErrorText={getValidatedGroupLabelErrorMessage} />
    )
}

export const getValidatedLabelErrorMessage = (newLabel: string, existingLabels?: string[]) => {
    if (!newLabel) {
        return 'Label cannot be empty';
    }

    const invalidTagMessage = getValidatedDecodedTagErrorMessage(newLabel);
    if (!!invalidTagMessage) {
        return invalidTagMessage;
    }

    if (!!existingLabels?.find(existing => stringEqualsIgnoreCase(existing, newLabel.trim()))) {
        return 'This label is already in use.';
    }

    return '';
}

const stringEqualsIgnoreCase = (a: string, b: string): boolean => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()) === 0;